/**APIの基本レスポンス */
export type APIResponse<T> = {
  code: number;
  message: string;
  errors: Array<{ [key: string]: Array<string> }>;
  result: T;
};

/**ページネーション時のresult */
export type APIResponseResultPagination<T> = {
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  data: Array<T>;
};

/**カーソルページネーション時のresult */
export type APIResponseResultCursor<T> = {
  data: Array<T>;
  hasMore: boolean;
  totalCount: number;
};

/**カーソルページネーション共通リクエストパラメータ */
export type CursorPaginationParams = {
  limit?: number;
  lastId?: number;
  direction?: 'next' | 'prev';
};

export enum ItemType {
  coin = 'coin',
  oemSubscription = 'subscription',
  artistSubscription = 'artist_subscription',
  digitalContent = 'digital_content',
}

export type CartItemMaterial = {
  materialId: number;
  materialName: string;
  materialPrice: number;
};

export type CartItem = {
  id: number;
  productId: number;
  productName: string;
  productDescription: string;
  workId: number;
  workName: string;
  userPrintId: number;
  previewImageUrl: string;
  quantity: number;
  selectedColorId: number;
  selectedColorName: string;
  selectedSizeId: number;
  selectedSizeName: string;
  basePrice: number;
  price: number;
  subtotal: number;
  materials: CartItemMaterial[];
  isEnabled: boolean;
  prints: CartPrintItem[];
  productSpecs: ProductSpec[];
  productSaleStatus: SaleStatus;
  /** テンプレ商品（アクスタ・シール）のときのみ値が入る。constants の PRODUCT_TYPE と整合させる */
  productType?: 'design_tool' | 'template' | 'die_cut';
};

export type CartPrintItem = {
  printId: number;
  printName: string;
  printPrice: number;
  previewImageUrl: string;
  updatedAt: string;
};

export type LocalCartItem = {
  userPrintId: number;
  quantity: number;
  timestamp: number;
};

// NOTE: LocalCartInfo の構成が変更された場合は環境変数 NEXT_PUBLIC_LOCAL_CART_VERSION を更新
export type LocalCartInfo = {
  cartVersion: number;
  items?: LocalCartItem[];
};

export type CartInfo = {
  id: number;
  items: CartItem[];
  totalItems: number;
  totalQuantity: number;
  subtotal: number;
  tax: number;
  shippingFee: number;
  minShippingFee: number;
  total: number;
  updatedAt: string;
  hasExpiredItems?: boolean;
  expiredItemsCount?: number;
  expiredReason?: string | null;
  expiredMessage?: string | null;
};

export type ProductSpec = {
  key: string;
  value: string;
};

/** みんなのデザイン一覧の作家情報 */
export type UserDesignListCreator = {
  id: number;
  name: string;
  copyrightShort: string;
};

/** みんなのデザイン一覧のプリント情報 */
export type UserDesignPrint = {
  id: number;
  name: string;
  previewImageUrl: string;
};

export type UserDesignItem = {
  id: number;
  /** ユーザープリントID（Sqids難読化） */
  userPrintId: string;
  gfWorkId: number;
  gfWorkName: string;
  workLogoURL: string | null;
  creators: UserDesignListCreator[];
  productId: number;
  productName: string;
  /** 商品タイプ（template はカラーのみ表示・サイズ非表示） */
  productType?: string;
  productColorId: number;
  productColorName: string;
  productSizeId: number;
  productSizeName: string;
  materials: UserDesignMaterial[];
  previewImageUrl: string;
  createdAt: string;
  prints: UserDesignPrint[];
};

export type UserDesignMaterial = {
  id: number;
  name: string;
};

export type UserDesignSlideItem = {
  id: number;
  items: UserDesignItem[];
};

/** みんなのデザインスライダーのモーダルタイプ */
export const USER_DESIGN_MODAL_TYPE = {
  IMAGE_DETAIL: 'imageDetail',
  DESIGN_DETAIL: 'designDetail',
} as const;

export type UserDesignModalType = (typeof USER_DESIGN_MODAL_TYPE)[keyof typeof USER_DESIGN_MODAL_TYPE];

export type Creator = {
  id: number;
  name: string;
};

export type Magazine = {
  id: number;
  name: string;
};

// バッジステータスの定義
export const BadgeStatus = {
  new: { value: 'new', color: 'red', text: 'NEW' },
  added: { value: 'added', color: 'orange', text: 'コマ追加' },
} as const;
export type BadgeStatusType = keyof typeof BadgeStatus;

export type GfWork = {
  id: number;
  name: string;
  nameKana: string;
  nameEn: string;
  description: string;
  imagePath: string;
  thumbnailPath: string;
  startDate: string;
  endDate: string;
  startIssueNumber: number;
  endIssueNumber: number;
  badgeStatus: BadgeStatusType | null;
  latestMaterialAddedAt: string;
  creators: Creator[];
  magazines: Magazine[];
  createdAt: string;
  updatedAt: string;
};

export type GfWorkWithMaterials = GfWork & {
  materials: {
    id: number;
    name: string;
    thumbnailPath: string;
  }[];
};

export type GfPrintAreaItemForDesign = {
  id: number;
  name: string;
  description: string;
  x: number;
  y: number;
  width: number;
  height: number;
  previewImagePath: string;
  modelMappingData: string;
  additionalTaxExcludedPrice: number;
  additionalTaxIncludedPrice: number;
  status: string;
  defaultScale: number;
  printAreaKey: string;
  colorPreviewImages: {
    productColorId: number;
    productColorRelationId: number;
    previewImageName: string;
    previewImagePath: string;
    displayOrder: number;
  }[];
};

export type GfProductDetailForDesign = {
  id: number;
  name: string;
  basePrice: number;
  taxExcludedPrice: number;
  model3dPath: string;
  bodyMappingData: string;
  sizeTable: GfSizeTableData | null;
  printAreas: GfPrintAreaItemForDesign[];
  variations: {
    colors: GfProductColor[];
    sizes: GfProductSize[];
  };
};

export type GfProductColor = {
  id: number;
  name: string;
  colorCode: string;
  displayOrder: number;
  additionalTaxExcludedPrice: number;
  additionalTaxIncludedPrice: number;
  status: string;
  gridColor?: string;
  model3dPath: string;
  bodyMappingData?: string;
};

export type GfProductSize = {
  id: number;
  name: string;
  displayOrder: number;
  description: string;
  additionalTaxExcludedPrice: number;
  additionalTaxIncludedPrice: number;
  sizeScaleFactor: number;
  status: string;
};

export type GfSizeTableData = {
  headers: Header[];
  rows: Row[];
};

export type Header = {
  key: string;
  label: string;
};

export type Row = {
  id: number;
  [key: string]: string | number;
};

export type OemService = {
  oemServiceName: string;
  imageUrl: string;
  iconUrl: string;
  serviceUrl: string;
  keyColor: string;
  oemServiceBranches: {
    branchKey: string;
    branchUrl: string;
    defaultFlag: boolean;
    imageUrl: string;
    keyColor: string;
    oemServiceBranchName: string;
  }[];
};

export type ArticleTag = {
  id: number;
  name: string;
};

export type ArticleArtist = {
  id: number;
  name: string;
  imageUrl: string;
};

export type Article = {
  id: number;
  title: string;
  publishedAt: string;
  mainImageUrl: string;
  excerpt: string;
  tag: Array<ArticleTag>;
  artist: Array<ArticleArtist>;
  targetType: string;
};

export const ImageType = {
  main: 'main',
  sub: 'sub',
  icon: 'icon',
  iconSub: 'icon_sub',
} as const;

export type ImageType = keyof typeof ImageType;

export const MaterialFilter = {
  ALL: 'all',
  CHARACTER: 'character',
  FRAME: 'frame',
} as const;

export type MaterialFilterTabValue = (typeof MaterialFilter)[keyof typeof MaterialFilter];

export type Spec = {
  id: number;
  key: string;
  value: string;
};

// 販売ステータスの型定義
export type SaleStatus = 'on_sale' | 'discontinued' | 'coming_soon';

export type BlankPrintArea = {
  printAreaId: number;
  name: string;
  bodyImageUrl: string;
};

export type ShippingAddressFormValues = {
  /** 姓（family name） */
  lastName: string;
  /** 名（given name） */
  firstName: string;
  /** 姓カナ（family name kana） */
  lastNameKana: string;
  /** 名カナ（given name kana） */
  firstNameKana: string;
  phone: string;
  zip: string;
  province: string;
  city: string;
  address1: string;
  address2: string;
  saveAddress: string[];
};

/**
 * 商品一覧のカラー／背景バリエーション。商品タイプによって表示方法が排他になる。
 * - 非テンプレ商品: colorCode に色コードを持つ
 * - テンプレ商品（ステッカー＝templates / アクスタ＝printPresets）: swatchImagePath にスウォッチ画像URLを持つ
 */
export type ProductColorVariant =
  | { id: number; name: string; displayOrder: number; colorCode: string; swatchImagePath?: never }
  | { id: number; name: string; displayOrder: number; swatchImagePath: string; colorCode?: never };

export type Product = {
  id: number;
  name: string;
  /** 商品タイプ。template はサイズ概念がないため一覧でサイズ表示しない。constants の PRODUCT_TYPE と整合させる */
  productType?: 'design_tool' | 'template' | 'die_cut';
  description: string;
  basePrice: number;
  taxExcludedPrice: number;
  model3dPath: string;
  bodyMappingData: string;
  displayOrder: number;
  imageUrls: {
    [key in ImageType]: string;
  };
  latestUserDesignImageUrl: string | null;
  specs: Spec[];
  workName: string | null;
  colors: ProductColorVariant[];
  sizes: {
    id: number;
    name: string;
    displayOrder: number;
  }[];
  minimumPrice: number;
  status: {
    isHot: boolean;
    isNew: boolean;
  };
  createdAt: string;
  updatedAt: string;
  categories: {
    id: number;
    name: string;
    displayOrder: number;
  }[];
  saleStatus: SaleStatus;
};
